import { useEffect, useState, useContext } from 'react'
import { Skeleton, Box } from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import _ from 'lodash'

import AppleFront from '/components/editor/apple/front'
import AppleLockScreen from '/components/editor/apple/lock-screen'
import AppleBack from '/components/editor/apple/back'

import GoogleFront from '/components/editor/google/front'
import GoogleListItem from '/components/editor/google/list-item'
import GoogleBack from '/components/editor/google/back'

import GoogleAppLink from '/components/editor/google/fronts/components/app-link'

import Settings from './settings'
import detectMinFontSizeOverride from '/utils/detect-min-font-size-override.js'

import { AppContext } from "/providers/app-context"
import { PassContext } from "./pass-context"

const AppBody = ({ isSmartixScannerPass, templateLoaded, templateId, smartixPassType, appleModel, appleTemplate, googleObject, instanceData, setInstanceData, scannerData, setScannerData, scannerInstanceData, setScannerInstanceData, googleClass, applePassType, appleSubType, googlePassType, googleSubType, setAppleModel, setAppleTemplate, setGoogleClass, setGoogleObject, syncBarcodeData, syncBackgroundColour, metaData, setMetaData, setGoogleBoth, openInstanceDataManager }) => {

  const { client, user, saveUser } = useContext(AppContext)

  const clientId = client?.id

  useEffect(() => {
    const info = detectMinFontSizeOverride()
    if (info.overridden) {
      enqueueSnackbar({
        message: <>
          Preview note: your browser’s minimum text size setting is active.<br />
          See Settings &gt; Advanced &gt; Never use font sizes less than...<br />
          <br />
          Smaller fonts (eg. labels) may be rendered larger than they will appear on devices.<br />
          <br />
          We suggest you either disable this feature whilst using the editor or switch to Chrome browser.
        </>, variant: 'warning', persist: true
      })
    }
  }, [])

  const toggleEditAreaVisibility = () => {
      console.log("toggle")
    let u = _.cloneDeep(user)
    u.preferences.highlight_editable_items = !u.preferences.highlight_editable_items
    saveUser(u)
  }

  useEffect(() => {
    if (!user) return

    const handleKeydown = event => {
      // Ctrl+Alt/Option+E to toggle edit area highlighting
      if (
        event.code === 'KeyE' &&
        event.ctrlKey &&
        event.altKey
      ) {
        event.preventDefault()
        toggleEditAreaVisibility()
      }
    }

    window.addEventListener('keydown', handleKeydown)
    return () => window.removeEventListener('keydown', handleKeydown)
  }, [user])

  return (
    <div className='flex flex-row justify-between gap-10 w-screen ml-5 h-full overflow-y-scroll '>
      <div className='flex flex-wrap gap-5h-full pt-5 pb-10'>
        <div className='w-[400px] flex flex-col gap-10'>
          <div className="apple">
            <div>Apple IOS Lock Screen ({applePassType})</div>

            <div className="wrapper"> {/* Put apple pass on a grey background with drop shadow */}
              <div className="pass">

                {templateLoaded &&
                  <PassContext.Provider value={{ openInstanceDataManager, device: "apple", clientId, templateId, smartixPassType, model: appleModel, template: appleTemplate, passType: applePassType, subType: appleSubType, setModel: setAppleModel, setTemplate: setAppleTemplate, syncBarcodeData, syncBackgroundColour, instanceData, setInstanceData, scannerInstanceData, setScannerInstanceData, scannerData, setScannerData }}>
                    <AppleLockScreen />
                  </PassContext.Provider>
                }

                {!templateLoaded && <Skeleton variant="rectangular" width="100%" height={100} />}
              </div>
            </div>
          </div>

          <div className="apple">

            <div>Apple IOS Front ({applePassType})</div>

            <div className="wrapper"> {/* Put apple pass on a grey background with drop shadow */}
              <div className="pass"> {/* id="appleFront" required for the image grab for the minature at the top of the back fields*/}

                {templateLoaded &&
                  <PassContext.Provider value={{ openInstanceDataManager, device: "apple", clientId, templateId, smartixPassType, isSmartixScannerPass, model: appleModel, template: appleTemplate, passType: applePassType, subType: appleSubType, setModel: setAppleModel, setTemplate: setAppleTemplate, syncBarcodeData, syncBackgroundColour, metaData, setMetaData, instanceData, setInstanceData, scannerInstanceData, setScannerInstanceData, scannerData, setScannerData }}>
                    <AppleFront />
                  </PassContext.Provider>
                }

                {!templateLoaded && <Skeleton variant="rectangular" width="100%" height={600} />}

              </div>
            </div>
          </div>

          <div className="apple">
            <div>Apple IOS Back</div>

            <div className="wrapper mt-5" style={{ borderRadius: 10 }}> {/* Put apple pass on a grey background with drop shadow */}
              <div className="pass -mt-5">

                {templateLoaded &&
                  <PassContext.Provider value={{ openInstanceDataManager, device: "apple", clientId, templateId, smartixPassType, isSmartixScannerPass, model: appleModel, template: appleTemplate, passType: applePassType, subType: appleSubType, setModel: setAppleModel, setTemplate: setAppleTemplate, instanceData, setInstanceData, scannerInstanceData, setScannerInstanceData, scannerData, setScannerData }}>
                    <AppleBack />
                  </PassContext.Provider>
                }

                {!templateLoaded && <Skeleton variant="rectangular" width="100%" height={600} />}

              </div>
            </div>
          </div>
        </div>

        <div className='w-[470px] flex flex-col gap-10'>
          <div className="google">
            <div>Google Android List item ({googlePassType})</div>

            <div className="wrapper"> {/* Put apple pass on a grey background with drop shadow */}
              <div className="pass">

                {templateLoaded &&
                  <PassContext.Provider value={{ openInstanceDataManager, device: "google", clientId, templateId, smartixPassType, classData: googleClass, objectData: googleObject, passType: googlePassType, subType: googleSubType, setClassData: setGoogleClass, setObjectData: setGoogleObject, syncBarcodeData, syncBackgroundColour, instanceData, setInstanceData, scannerInstanceData, setScannerInstanceData, scannerData, setScannerData }}>
                    <GoogleListItem />
                  </PassContext.Provider>
                }

                {!templateLoaded && <Skeleton variant="rectangular" width="100%" height={100} />}

              </div>
            </div>
          </div>

          <div className="google">
            <div>Google Android Front ({googlePassType})</div>

            <div className="wrapper"> {/* Put apple pass on a grey background with drop shadow */}
              <div className="pass">

                {templateLoaded &&
                  <PassContext.Provider value={{ openInstanceDataManager, device: "google", clientId, templateId, smartixPassType, isSmartixScannerPass, classData: googleClass, objectData: googleObject, passType: googlePassType, subType: googleSubType, setClassData: setGoogleClass, setObjectData: setGoogleObject, syncBarcodeData, syncBackgroundColour, metaData, setMetaData, setGoogleBoth, instanceData, setInstanceData, scannerInstanceData, setScannerInstanceData, scannerData, setScannerData }}>
                    <GoogleFront />
                  </PassContext.Provider>
                }

                {!templateLoaded && <Skeleton variant="rectangular" width="100%" height={600} />}

              </div>

              {templateLoaded &&
                <PassContext.Provider value={{ openInstanceDataManager, classData: googleClass, objectData: googleObject, passType: googlePassType, setClassData: setGoogleClass, setObjectData: setGoogleObject, setGoogleBoth }}>
                  <GoogleAppLink />
                </PassContext.Provider>
              }

            </div>
          </div>

          <div className="google">
            <div>Google Android Back</div>
            <div className="wrapper"> {/* Put apple pass on a grey background with drop shadow */}
              <div className="pass">

                {templateLoaded &&
                  <PassContext.Provider value={{ openInstanceDataManager, device: "google", clientId, templateId, smartixPassType, isSmartixScannerPass, classData: googleClass, objectData: googleObject, passType: googlePassType, subType: googleSubType, setClassData: setGoogleClass, setObjectData: setGoogleObject, setGoogleBoth, instanceData, setInstanceData, scannerInstanceData, setScannerInstanceData, scannerData, setScannerData }}>
                    <GoogleBack />
                  </PassContext.Provider>
                }

                {!templateLoaded && <Skeleton variant="rectangular" width="100%" height={600} />}

              </div>
            </div>
          </div>

        </div>
      </div>

      <Box sx={{ position: "sticky", top: 0, right: 20, bottom: 0, width: "390px", paddingBottom: 10, zIndex:1100 }} >
        <PassContext.Provider value={{ openInstanceDataManager, smartixPassType, applePassType, appleSubType, googlePassType, instanceData, model: appleModel, template: appleTemplate, setModel: setAppleModel, setTemplate: setAppleTemplate, classData: googleClass, objectData: googleObject, setClassData: setGoogleClass, setObjectData: setGoogleObject, metaData, setMetaData }}>
          <Settings />
        </PassContext.Provider>
      </Box>

    </div >
  )
}

export default AppBody
